import clsx from 'clsx';
import type { ButtonHTMLAttributes } from 'react';
import { Icon, type IconName } from '../Icon/Icon';
import styles from './IconButton.module.css';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** Required: the accessible name. */
  label: string;
  iconSize?: number;
  strokeWidth?: number;
  /** Red count bubble. Hidden when 0 or undefined. */
  badge?: number;
  /** Badge colour override (the filter count uses the primary colour). */
  badgeTone?: 'danger' | 'primary';
  /** On the transparent header over the hero. */
  overHero?: boolean;
  size?: 44 | 42;
}

/** 44 px round icon button (`.ib`). */
export function IconButton({
  icon,
  label,
  iconSize = 19,
  strokeWidth = 1.9,
  badge,
  badgeTone = 'danger',
  overHero,
  size = 44,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      className={clsx(styles.ib, size === 42 && styles.s42, overHero && styles.over, className)}
      {...rest}
    >
      <Icon name={icon} size={iconSize} strokeWidth={strokeWidth} />
      {badge ? <Badge count={badge} tone={badgeTone} /> : null}
    </button>
  );
}

export function Badge({
  count,
  tone = 'danger',
  className,
}: {
  count: number;
  tone?: 'danger' | 'primary';
  className?: string;
}) {
  // Capped like the current site's unread badge (CH-01): «99+».
  return (
    <span className={clsx(styles.badge, tone === 'primary' && styles.badgePrimary, className)}>
      {count > 99 ? '99+' : count}
    </span>
  );
}
